import { useState } from 'react';
import { ArrowUpDown, TrendingUp, Shield, Zap, Target } from 'lucide-react';
import { useTopOpportunities, TokenOpportunity } from '@/hooks/useTopOpportunities';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const OpportunitiesTable = () => {
  const [filterRecommendation, setFilterRecommendation] = useState<string>('all');
  const [filterNarrative, setFilterNarrative] = useState<string>('all');
  
  const { data: opportunities, isLoading, error } = useTopOpportunities({
    limit: 30,
    recommendation: filterRecommendation === 'all' ? undefined : filterRecommendation,
    narrative: filterNarrative === 'all' ? undefined : filterNarrative,
  });

  const getRecommendationColor = (recommendation: string | null) => {
    switch (recommendation) {
      case 'strong_buy': return 'bg-green-500/20 text-green-400 border-green-500/50';
      case 'buy': return 'bg-green-500/10 text-green-300 border-green-500/30';
      case 'speculative_buy': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
      case 'watch': return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getRiskLevelColor = (risk: string | null) => {
    switch (risk) {
      case 'low': return 'bg-green-500/10 text-green-400';
      case 'medium': return 'bg-yellow-500/10 text-yellow-400';
      case 'high': return 'bg-orange-500/10 text-orange-400';
      case 'very_high': return 'bg-red-500/10 text-red-400';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getNarrativeIcon = (narrative: string | null) => {
    switch (narrative) {
      case 'momentum_play': return <TrendingUp className="h-3 w-3" />;
      case 'technical_breakout': return <Zap className="h-3 w-3" />;
      case 'undervalued_gem': return <Target className="h-3 w-3" />;
      case 'viral_trending': return <TrendingUp className="h-3 w-3" />;
      default: return <Shield className="h-3 w-3" />;
    }
  };

  const formatNarrative = (narrative: string | null) => {
    if (!narrative) return 'General';
    return narrative.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Investment Opportunities</CardTitle>
          <CardDescription>Error loading opportunities: {error.message}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Target className="h-6 w-6 text-primary" />
              Top Investment Opportunities
            </CardTitle>
            <CardDescription>
              AI-scored tokens with comprehensive analysis
            </CardDescription>
          </div>
          
          <div className="flex gap-2">
            <Select value={filterRecommendation} onValueChange={setFilterRecommendation}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Recommendations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Recommendations</SelectItem>
                <SelectItem value="strong_buy">Strong Buy</SelectItem>
                <SelectItem value="buy">Buy</SelectItem>
                <SelectItem value="speculative_buy">Speculative Buy</SelectItem>
                <SelectItem value="watch">Watch</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filterNarrative} onValueChange={setFilterNarrative}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Narratives" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Narratives</SelectItem>
                <SelectItem value="momentum_play">Momentum Play</SelectItem>
                <SelectItem value="undervalued_gem">Undervalued Gem</SelectItem>
                <SelectItem value="technical_breakout">Technical Breakout</SelectItem>
                <SelectItem value="viral_trending">Viral Trending</SelectItem>
                <SelectItem value="defi_innovation">DeFi Innovation</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : opportunities && opportunities.length > 0 ? (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Token</TableHead>
                  <TableHead className="text-right">Score</TableHead>
                  <TableHead>Recommendation</TableHead>
                  <TableHead>Risk</TableHead>
                  <TableHead>Narrative</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead className="text-right">24h</TableHead>
                  <TableHead>Signals</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {opportunities.map((opp) => (
                  <TableRow key={opp.id} className="cursor-pointer hover:bg-muted/50">
                    <TableCell className="font-medium">
                      <div>
                        <div className="font-semibold">{opp.token_symbol}</div>
                        <div className="text-xs text-muted-foreground">{opp.token_name}</div>
                      </div>
                    </TableCell>
                    
                    <TableCell className="text-right">
                      <div className="flex flex-col items-end">
                        <span className="text-lg font-bold text-primary">
                          {Math.round(opp.opportunity_score)}
                        </span>
                        <span className="text-xs text-muted-foreground">/ 100</span>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <Badge className={getRecommendationColor(opp.recommendation)}>
                        {opp.recommendation?.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    
                    <TableCell>
                      <Badge variant="outline" className={getRiskLevelColor(opp.risk_level)}>
                        {opp.risk_level?.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        {getNarrativeIcon(opp.narrative)}
                        <span>{formatNarrative(opp.narrative)}</span>
                      </div>
                    </TableCell>
                    
                    <TableCell className="text-right font-mono">
                      ${opp.current_price.toFixed(opp.current_price < 1 ? 4 : 2)}
                    </TableCell>
                    
                    <TableCell className="text-right">
                      {opp.price_change_24h !== null && (
                        <span className={opp.price_change_24h >= 0 ? 'text-green-400' : 'text-red-400'}>
                          {opp.price_change_24h >= 0 ? '+' : ''}{opp.price_change_24h.toFixed(2)}%
                        </span>
                      )}
                    </TableCell>
                    
                    <TableCell>
                      <div className="flex gap-1">
                        {opp.buy_signals.slice(0, 3).map((signal, idx) => (
                          <Badge 
                            key={idx} 
                            variant="outline" 
                            className="text-xs"
                            title={signal.description}
                          >
                            {signal.type}
                          </Badge>
                        ))}
                        {opp.buy_signals.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{opp.buy_signals.length - 3}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No opportunities found. Try running the enhanced market analysis.
          </div>
        )}
      </CardContent>
    </Card>
  );
};
